import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useReportingApi } from '../api/context'
import { REPORTING_QUERY_KEYS } from '../api/endpoints'
import type { ExtensionLineEntry } from '../api/reportingApi'

interface UseExtensionNamesParams {
  pbxId: number
  enabled?: boolean
}

export type ExtensionNameMap = Map<string, string>

interface UseExtensionNamesResult {
  nameMap: ExtensionNameMap
  isLoading: boolean
  isFetching: boolean
}

export const useExtensionNames = ({
  pbxId,
  enabled = true,
}: UseExtensionNamesParams): UseExtensionNamesResult => {
  const api = useReportingApi()

  const {
    data: extensionLines,
    isLoading,
    isFetching,
  } = useQuery<ExtensionLineEntry[]>({
    queryKey: [REPORTING_QUERY_KEYS.extensionLines, pbxId],
    queryFn: async () => {
      const { ok, data } = await api.getExtensionLines({ pbx_id: pbxId })
      if (!ok || !data) return []
      return data
    },
    enabled: enabled && pbxId > 0,
    staleTime: 5 * 60 * 1000,
  })

  const nameMap = useMemo<ExtensionNameMap>(() => {
    const map = new Map<string, string>()
    if (!extensionLines) return map
    for (const line of extensionLines) {
      map.set(line.number, line.name)
    }
    return map
  }, [extensionLines])

  return { nameMap, isLoading, isFetching }
}

/**
 * @param fallbackSuffix - If provided, appended in parens when no name found (e.g. "Retired").
 *   When omitted, unknown extensions are returned as bare numbers.
 */
export function formatExtensionLabel(
  extension: string,
  nameMap: ExtensionNameMap,
  fallbackSuffix?: string,
): string {
  const name = nameMap.get(extension)
  if (name) return `${name} (${extension})`
  if (fallbackSuffix) return `${extension} (${fallbackSuffix})`
  return extension
}
